import {
  FormSelect,
  FormSelectOption,
  Grid,
  GridItem,
  TextInput,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import {
  getQuestionValidators,
  getResponseErrorsForStringValidators,
} from '@app/domain/form/form.helpers'
import type { UnitRecord } from '@app/domain/form/form.interface'
import type { Question } from '@app/domain/instrument'
import { ValidationStatusId } from '@app/lib/validators'
import type { AppState } from '@app/state'
import type { QuestionsConfig } from '@app/state/config/interfaces'

import { FormGroup } from '../form-inputs'

const TextField = styled(TextInput)`
  padding: 8px 16px 8px 16px;
  border: 2px solid ${(props) => props.theme.colors.primary};
  border-radius: 5px;
  vertical-align: middle;
  appearance: none;
`

const SelectField = styled(FormSelect)`
  border: 2px solid ${(props) => props.theme.colors.primary};
  border-radius: 5px;
  vertical-align: middle;
  appearance: none;
`

const UnitFieldsContainer = styled.div`
  max-width: 80%;
  margin: 0 auto;
`

const FormGroupHelperTextInvalid = styled.label`
  margin-left: 10%;
  color: #a30000;
`

interface Props {
  key: number | string
  onChange: (value: UnitRecord) => void
  question: Question
  value: Record<string, string>
}

export const UnitQuestion = ({ key, onChange, question, value }: Props) => {
  const defaultSelectType = 'Please Choose'

  const questionResponsesConfig = useSelector<AppState, QuestionsConfig>(
    (state) => state.config.api.questions,
  )

  const validators = useMemo(() => {
    return getQuestionValidators(question)
  }, [question])

  const [stringValue, setStringValue] = useState<string>(value?.value ?? null)
  const [unitValue, setUnitValue] = useState<string>(value?.unit ?? null)

  const [textInputValidation, setTextInputValidation] = useState<
    ValidationStatusId
  >(ValidationStatusId.Default)
  const [errorMessage, setErrorMessage] = useState<string>(null)

  const handleBlur = useCallback(() => {
    const updatedValue = { value: stringValue, unit: unitValue }
    onChange(updatedValue)
  }, [onChange, stringValue, unitValue])

  const handleChange = useCallback(
    (updatedStringValue: string) => {
      setStringValue(updatedStringValue)
      let inputIsValid = false
      if (updatedStringValue !== undefined) {
        const errors = getResponseErrorsForStringValidators(
          validators,
          updatedStringValue,
          questionResponsesConfig,
        )
        inputIsValid = errors.length === 0
        setErrorMessage(errors[0])
      }

      inputIsValid
        ? setTextInputValidation(ValidationStatusId.Default)
        : setTextInputValidation(ValidationStatusId.Error)

      const updatedValue = { value: updatedStringValue, unit: unitValue }

      onChange(updatedValue)
    },
    [unitValue, onChange, validators, questionResponsesConfig],
  )

  const handleUnitChange = useCallback(
    (updatedUnitValue: string) => {
      if (updatedUnitValue === defaultSelectType) {
        updatedUnitValue = ''
      }
      const updatedValue = { value: stringValue, unit: updatedUnitValue }
      setUnitValue(updatedUnitValue)
      onChange(updatedValue)
    },
    [onChange, stringValue],
  )

  return (
    <UnitFieldsContainer>
      <FormGroup
        key={`question-${key}`}
        fieldId={question.id}
        label={question.label}
        details={question.details}
        stretch={'stretch'}
      >
        <Grid hasGutter>
          <GridItem span={8}>
            <TextField
              id={question.id}
              name={question.id}
              value={stringValue}
              validated={textInputValidation}
              onBlurCapture={handleBlur}
              onChange={handleChange}
            />
          </GridItem>
          <GridItem span={4}>
            <SelectField
              id="unit"
              isRequired
              value={unitValue}
              onChange={handleUnitChange}
              onBlur={handleBlur}
            >
              <FormSelectOption
                key={0}
                value={null}
                label={defaultSelectType}
              />
              {question.options.map((option) => (
                <FormSelectOption
                  id={`${question.id}_${option.value}`}
                  key={`${question.id}_${option.value}`}
                  value={option.value}
                  label={option.label}
                />
              ))}
            </SelectField>
          </GridItem>
        </Grid>
        <FormGroupHelperTextInvalid>{errorMessage}</FormGroupHelperTextInvalid>
      </FormGroup>
    </UnitFieldsContainer>
  )
}
